import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import {
  MOVEMENT_REASONS,
  REASON_LABELS,
  deleteProduct,
  exportMovements,
  formatCost,
  formatDelta,
  formatMoney,
  getProduct,
  listBalances,
  listLocations,
  listMovements,
  type Balance,
  type Movement,
} from '@/features/inventory/api'
import { toast } from '@/lib/toast'
import { formatPercent } from '@/lib/money'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/** The date and the time. A movement at 09:14 and one at 17:40 on the same day are two
 *  different events to whoever is reconciling a count, and a date alone hides that. */
const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  )
}

const balanceColumns = (nameOf: (id: string) => string): Column<Balance>[] => [
  {
    key: 'location',
    header: 'Location',
    className: 'font-medium',
    sortValue: (balance) => nameOf(balance.location_id),
    cell: (balance) => nameOf(balance.location_id),
  },
  {
    key: 'quantity',
    header: 'On hand',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    sortValue: (balance) => balance.quantity,
    cell: (balance) => balance.quantity,
  },
  {
    key: 'reserved',
    header: 'Reserved',
    className: 'text-right tabular-nums text-slate-500 dark:text-slate-400',
    headerClassName: 'text-right',
    cell: (balance) => balance.reserved_quantity,
  },
  {
    key: 'available',
    // The number that decides whether the next order can be taken. On hand alone is the
    // figure people quote, and the one that oversells the shelf.
    header: 'Available',
    className: 'text-right font-medium tabular-nums',
    headerClassName: 'text-right',
    sortValue: (balance) => balance.available,
    cell: (balance) => balance.available,
  },
  {
    key: 'reorder_point',
    header: 'Reorder at',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (balance) =>
      balance.quantity <= balance.reorder_point ? (
        <span className="rounded bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
          {balance.reorder_point} · low
        </span>
      ) : (
        <span className="text-slate-500 dark:text-slate-400">{balance.reorder_point}</span>
      ),
  },
  {
    key: 'avg_cost',
    header: 'Avg cost',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (balance) => formatCost(balance.avg_cost),
  },
  {
    key: 'stock_value',
    header: 'Value',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    sortValue: (balance) => Number(balance.stock_value),
    cell: (balance) => formatMoney(balance.stock_value),
  },
]

const movementColumns = (nameOf: (id: string) => string): Column<Movement>[] => [
  {
    key: 'created_at',
    header: 'When',
    className: 'whitespace-nowrap text-slate-500 dark:text-slate-400',
    sortValue: (movement) => movement.created_at,
    cell: (movement) => formatWhen(movement.created_at),
  },
  {
    key: 'location',
    header: 'Location',
    cell: (movement) => nameOf(movement.location_id),
  },
  {
    key: 'reason',
    header: 'Reason',
    cell: (movement) => REASON_LABELS[movement.reason] ?? movement.reason,
  },
  {
    key: 'qty_delta',
    header: 'Change',
    className: 'text-right font-medium tabular-nums',
    headerClassName: 'text-right',
    sortValue: (movement) => movement.qty_delta,
    cell: (movement) => (
      <span className={movement.qty_delta < 0 ? 'text-danger' : 'text-success'}>
        {formatDelta(movement.qty_delta)}
      </span>
    ),
  },
  {
    key: 'unit_cost',
    header: 'Unit cost',
    className: 'text-right tabular-nums text-slate-500 dark:text-slate-400',
    headerClassName: 'text-right',
    cell: (movement) => (movement.unit_cost === null ? '—' : formatCost(movement.unit_cost)),
  },
  {
    key: 'ref',
    header: 'Source',
    className: 'text-slate-500 dark:text-slate-400',
    cell: (movement) => movement.ref_type?.replace(/_/g, ' ') ?? 'Manual',
  },
]

/**
 * One product: what it is, where its stock sits, and every movement behind that figure.
 *
 * The ledger is on the same screen as the balance on purpose. The balance is a cache of
 * the movements, and the first question anyone asks about a number they distrust is "where
 * did it come from" — sending them to another screen to find out is how people end up
 * counting the shelf instead of reading the system.
 */
export function ProductDetail() {
  const { productId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [page, setPage] = useState(1)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const can = useAuth((s) => s.can)

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProduct(productId),
    retry: false,
  })

  const remove = useMutation({
    mutationFn: () => deleteProduct(productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Product discontinued.')
      navigate('/products')
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(false)
    },
  })
  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const balances = useQuery({
    queryKey: ['balances', { product_id: productId }],
    queryFn: () => listBalances({ product_id: productId }),
    retry: false,
  })
  const movements = useQuery({
    queryKey: ['movements', { product_id: productId, reason, page }],
    queryFn: () => listMovements({ product_id: productId, reason: reason || undefined, page }),
    retry: false,
  })

  const nameOf = (id: string) =>
    locations.data?.data.find((location) => location.id === id)?.name ?? '—'

  const rows = balances.data?.data ?? []
  const onHand = rows.reduce((total, balance) => total + balance.quantity, 0)
  const value = rows.reduce((total, balance) => total + Number(balance.stock_value), 0)
  const meta = movements.data?.meta

  return (
    <div className="space-y-4">
      <Link
        to="/products"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        All products
      </Link>

      <QueryState isLoading={product.isLoading} error={product.error}>
        {product.data && (
          <Card>
            <CardHeader
              title={product.data.name}
              description={product.data.description ?? undefined}
              action={
                <div className="flex items-center gap-2">
                  {can('products', 'update') && (
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/products/${productId}/edit`)}>
                      Edit
                    </Button>
                  )}
                  {can('products', 'delete') && product.data.status !== 'discontinued' && (
                    <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
                      Discontinue
                    </Button>
                  )}
                </div>
              }
            />
            <CardBody>
              <dl className="grid grid-cols-2 gap-4 tablet:grid-cols-4 desktop:grid-cols-6">
                <Fact label="Code" value={product.data.code} />
                <Fact label="Category" value={product.data.category_name ?? '—'} />
                <Fact label="Unit" value={product.data.unit_of_measure} />
                <Fact label="Price" value={formatMoney(product.data.price)} />
                <Fact label="Tax" value={formatPercent(product.data.tax_rate)} />
                <Fact label="On hand" value={`${onHand} ${product.data.unit_of_measure}`} />
              </dl>
              {product.data.long_description && (
                <p className="mt-4 whitespace-pre-line border-t border-slate-200 pt-4 text-sm leading-relaxed text-slate-600 dark:border-slate-800 dark:text-slate-400">
                  {product.data.long_description}
                </p>
              )}
            </CardBody>
          </Card>
        )}
      </QueryState>

      <Card>
        <CardHeader
          title="Stock by location"
          description={
            rows.length > 0
              ? `${onHand} on hand across ${rows.length} location${rows.length === 1 ? '' : 's'}, worth ${formatMoney(value)}.`
              : 'Every location this product has ever moved through.'
          }
        />
        <DataTable
          rows={rows}
          columns={balanceColumns(nameOf)}
          getRowId={(balance) => balance.id}
          caption="Stock by location"
          isLoading={balances.isLoading || locations.isLoading}
          error={balances.error}
          emptyMessage="This product has never been stocked anywhere."
          toolbar={
            can('inventory', 'update') ? (
              <Link to={`/stock/adjustments?product_id=${productId}`}>
                <Button variant="secondary" size="sm">
                  Correct stock
                </Button>
              </Link>
            ) : undefined
          }
        />
      </Card>

      <Card>
        <CardHeader
          title="Movements"
          description="Newest first. These add up to the figures above — nothing changes a balance without a line here."
        />
        <DataTable
          rows={movements.data?.data ?? []}
          columns={movementColumns(nameOf)}
          getRowId={(movement) => movement.id}
          caption="Stock movements"
          isLoading={movements.isLoading}
          error={movements.error}
          emptyMessage="No movements match this filter."
          toolbar={
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="movement-reason">
                Reason
              </label>
              <select
                id="movement-reason"
                className={SELECT_CLASS}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                  setPage(1)
                }}
              >
                <option value="">Every reason</option>
                {MOVEMENT_REASONS.map((option) => (
                  <option key={option} value={option}>
                    {REASON_LABELS[option]}
                  </option>
                ))}
              </select>

              <ExportButton
                filename={`stock_movements_${product.data?.code ?? productId}`}
                selectClassName={SELECT_CLASS}
                onExport={(format) =>
                  exportMovements({ product_id: productId, reason: reason || undefined }, format)
                }
                errorMessage="Could not export the movements."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: setPage,
                  isFetching: movements.isFetching,
                }
              : undefined
          }
        />
      </Card>


      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={product.data ? `Discontinue ${product.data.code}?` : 'Discontinue product'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
              Discontinue
            </Button>
          </>
        }
      >
        <Alert tone="info">
          This is a soft delete: the product is marked discontinued and hidden from new orders,
          but every past order and movement against it stays exactly as it was.
        </Alert>
      </Modal>
    </div>
  )
}
